// web/src/services/auth.ts
// Web auth service — mirrors mobile auth using same Cognito pool
// Uses Amplify v6 tree-shakeable imports (~45KB gzipped)

import { Amplify } from 'aws-amplify';
import {
  signIn, signUp, signOut, confirmSignUp,
  getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword,
} from 'aws-amplify/auth';

// Configure Amplify — same Cognito pool as mobile app
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (userPoolId && clientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
      },
    },
  });
}

export interface AuthState {
  isAuthenticated: boolean;
  user: { userId: string; email: string } | null;
  isLoading: boolean;
}

export const WebAuthService = {
  isConfigured(): boolean {
    return !!(userPoolId && clientId);
  },

  async signUp(email: string, password: string) {
    return signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
  },

  async confirmSignUp(email: string, code: string) {
    return confirmSignUp({ username: email, confirmationCode: code });
  },

  async signIn(email: string, password: string) {
    return signIn({ username: email, password });
  },

  async signOut() {
    await signOut();
  },

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  },

  async getSession() {
    try {
      return await fetchAuthSession();
    } catch {
      return null;
    }
  },

  async resetPassword(email: string) {
    return resetPassword({ username: email });
  },

  async confirmResetPassword(email: string, code: string, newPassword: string) {
    return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  },
};
